import React, { useCallback } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { Archive, Copy, Eye, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import type {
  FastRowLabelPrefix,
  FastRowRights,
} from "./useFastRowActionList.types";
import type { Query } from "../../api/BaseAPI";
import type { RowAction } from "../dataTable/dataTable.types";

interface UseFastRowActionListParams<
  Row extends { name: string; uuid: string },
  Details,
> {
  labelPrefix: FastRowLabelPrefix;
  getDetailsQuery: (row: Row) => Query<Details>;
  selectRights: (details: Details) => FastRowRights;
  getEditPath: (row: Row) => string;
  onDuplicate: (uuid: string) => void;
  onArchive: (uuid: string) => void;
  onDelete: (uuid: string) => void;
  isDuplicateDisabled?: (row: Row) => boolean;
}

export default function useFastRowActionList<
  Row extends { name: string; uuid: string },
  Details,
>({
  labelPrefix,
  getDetailsQuery,
  selectRights,
  getEditPath,
  onDuplicate,
  onArchive,
  onDelete,
  isDuplicateDisabled,
}: UseFastRowActionListParams<Row, Details>): (
  row: Row,
) => Promise<RowAction[]> {
  const { t } = useTranslation("editor-fast-channel");
  const history = useHistory();
  const queryClient = useQueryClient();

  // `staleTime: 0` : sinon le cache servirait d'anciens droits.
  return useCallback(
    async (row: Row): Promise<RowAction[]> => {
      const details = await queryClient.fetchQuery({
        ...getDetailsQuery(row),
        staleTime: 0,
      });
      const rights = selectRights(details);
      const { name, uuid } = row;

      const actionList: RowAction[] = [
        {
          label: rights.is_editable ? t("edit") : t("view"),
          icon: rights.is_editable ? <Pencil /> : <Eye />,
          onClick: () => history.push(getEditPath(row)),
        },
        {
          label: t("duplicate"),
          icon: <Copy />,
          disabled: isDuplicateDisabled?.(row),
          tooltip: isDuplicateDisabled ? t("duplicateDisabled") : undefined,
          onClick: () => onDuplicate(uuid),
        },
      ];

      // « Archiver » n'est pas destructif : pas de rouge.
      if (rights.is_archivable) {
        actionList.push({
          label: t("archive"),
          icon: <Archive />,
          onClick: () => onArchive(uuid),
          confirm: {
            title: t(`${labelPrefix}ActionModalTitle.archive`, { name }),
            description: t(`${labelPrefix}ActionModalText.archive`),
            confirmLabel: t("archive"),
          },
        });
      }

      if (rights.is_deletable) {
        actionList.push({
          label: t("delete"),
          icon: <Trash2 />,
          variant: "destructive",
          onClick: () => onDelete(uuid),
          confirm: {
            title: t(`${labelPrefix}ActionModalTitle.delete`, { name }),
            description: t(`${labelPrefix}ActionModalText.delete`),
            confirmLabel: t("delete"),
          },
        });
      }

      return actionList;
    },
    [
      queryClient,
      getDetailsQuery,
      selectRights,
      getEditPath,
      isDuplicateDisabled,
      labelPrefix,
      t,
      history,
      onDuplicate,
      onArchive,
      onDelete,
    ],
  );
}
